import { useCallback } from "react";
import { useKeysContext } from "./useKeysContext";
import { IKeysBlankNames } from "../constants/pragerohlinge";
import {
  IKeySideName,
  IKey,
  IKeyType,
  IKeySideFont,
  IKeySideLogoSize,
} from "../store/keysTypes";
import { KeyCustomizationHook } from "../contexts/KeysContextTypes";

export const useKeyCustomization = (): KeyCustomizationHook => {
  const { state, dispatch } = useKeysContext();
  const { selectedKeyTypes } = state;

  const getSelectedKeysArray = useCallback((): IKey[] => {
    return Object.values(selectedKeyTypes).flat();
  }, [selectedKeyTypes]);

  const getKeyByIndex = useCallback(
    (keyIndex: number, blank: IKeysBlankNames): IKey | undefined => {
      return selectedKeyTypes[blank]?.[keyIndex];
    },
    [selectedKeyTypes]
  );

  const setKeyVisited = useCallback(
    (keyIndex: number, blank: IKeysBlankNames, visited: boolean) => {
      dispatch({
        type: "SET_VISITED",
        payload: { keyIndex, visited, blank },
      });
    },
    [dispatch]
  );

  const updateKeyProperty = useCallback(
    (params: {
      keyIndex: number;
      blank: IKeysBlankNames;
      side?: IKeySideName;
      property: string;
      value: any;
      textIndex?: number;
    }) => {
      const { keyIndex, blank, side, property, value, textIndex } = params;

      // Handle different property types
      switch (property) {
        case "menge":
          dispatch({
            type: "SET_KEY_MENGE",
            payload: { keyIndex, value: value as string, blank },
          });
          break;

        case "type":
          if (side) {
            dispatch({
              type: "SET_KEY_SIDE_TYPE",
              payload: { keyIndex, side, type: value as IKeyType, blank },
            });
          }
          break;

        case "text":
          if (side && textIndex !== undefined) {
            dispatch({
              type: "SET_KEY_TEXT_FIELD",
              payload: {
                keyIndex,
                side,
                textIndex,
                value: value as string,
                blank,
              },
            });
          }
          break;

        case "font":
          if (side) {
            dispatch({
              type: "SET_KEY_FONT",
              payload: { keyIndex, side, value: value as IKeySideFont, blank },
            });
          }
          break;

        case "fontSize":
          if (
            side &&
            typeof value === "object" &&
            "name" in value &&
            "size" in value
          ) {
            dispatch({
              type: "SET_KEY_FONT_SIZE",
              payload: {
                keyIndex,
                name: value.name,
                side,
                value: value.size,
                blank,
              },
            });
          }
          break;

        case "logo":
          if (side) {
            dispatch({
              type: "SET_KEY_LOGO",
              payload: { keyIndex, side, value: value as string, blank },
            });
          }
          break;

        case "logoSize":
          if (side) {
            dispatch({
              type: "SET_KEY_LOGO_SIZE",
              payload: {
                keyIndex,
                side,
                value: value as IKeySideLogoSize,
                blank,
              },
            });
          }
          break;

        case "image":
          if (side) {
            dispatch({
              type: "SET_KEY_IMAGE",
              payload: { keyIndex, side, value: value as string, blank },
            });
          }
          break;

        case "screenshotReady":
          if (side) {
            dispatch({
              type: "SET_SCREENSHOT_READY",
              payload: { keyIndex, blank, side, value: value as boolean },
            });
          }
          break;

        default:
          console.warn(`Unknown property: ${property}`);
      }
    },
    [dispatch]
  );

  // Additional utility functions
  const addKey = useCallback(
    (key: IKey) => {
      dispatch({
        type: "SET_SELECTED_KEY_TYPES",
        payload: key,
      });
    },
    [dispatch]
  );

  const removeKey = useCallback(
    (value: string, blank: IKeysBlankNames) => {
      dispatch({
        type: "REMOVE_SELECTED_KEY_TYPE",
        payload: { value, blank },
      });
    },
    [dispatch]
  );

  const removeKeyById = useCallback(
    (id: string) => {
      dispatch({
        type: "REMOVE_KEY_BY_ID",
        payload: id,
      });
    },
    [dispatch]
  );

  const resetKeys = useCallback(() => {
    dispatch({ type: "RESET_KEYS" });
  }, [dispatch]);

  const setSelectedKeyId = useCallback(
    (keyId: string | null) => {
      dispatch({
        type: "SET_SELECTED_KEY_ID",
        payload: keyId,
      });
    },
    [dispatch]
  );

  // Computed values
  const selectedKeysArray = getSelectedKeysArray();
  const currentSelectedKey =
    selectedKeysArray.find((key) => key.id === state.selectedKeyId) || null;

  return {
    updateKeyProperty,
    setKeyVisited,
    getSelectedKeysArray,
    getKeyByIndex,
    addKey,
    removeKey,
    removeKeyById,
    resetKeys,
    setSelectedKeyId,
    selectedKeysArray,
    currentSelectedKey,
    selectedKeyTypes,
  };
};
